import { Injectable } from '@angular/core';
import { LocalStorage } from '@principle-theorem/ng-principle-shared';

export interface IRecorderPosition {
  x: number;
  y: number;
}

const RECORDER_POSITION_STORAGE_KEY = 'aiScribeRecorderPosition';
const VIEWPORT_MARGIN_PX = 48;

/**
 * Persists the AI Scribe recorder card's drag offset to localStorage so the
 * card reopens where the user last left it.
 *
 * The recorder host is anchored bottom-right (`right: 24px; bottom: 24px`), so
 * every legitimate free-drag offset is `<= 0` on both axes — the card only ever
 * moves left and up from its anchor. Reads are therefore validated defensively:
 * any stored value that is missing, non-finite, or positive is treated as
 * corrupt and discarded so the caller falls back to the default anchored
 * position.
 *
 * Exactly one {@link LocalStorage} instance is held as a field: its constructor
 * subscribes to the window `storage` event and only detaches on `destroy()`, so
 * constructing one per call would leak listeners.
 */
@Injectable({ providedIn: 'root' })
export class RecorderPositionStorageService {
  private _storage = new LocalStorage<IRecorderPosition>(
    RECORDER_POSITION_STORAGE_KEY
  );

  /**
   * Returns the last saved recorder offset, or `undefined` when nothing valid
   * is stored. Positive or non-finite coordinates are rejected because they
   * could push the card off-screen past its bottom-right anchor.
   */
  load(): IRecorderPosition | undefined {
    const stored = this._storage.value;
    if (stored === undefined) {
      return;
    }
    const isValid =
      RecorderPosition.isValidOffset(stored.x) &&
      RecorderPosition.isValidOffset(stored.y);
    return isValid ? stored : undefined;
  }

  /**
   * Persists the recorder offset through the LocalStorage wrapper so it survives
   * page reloads and syncs across tabs. Validation happens on read, not write.
   */
  save(position: IRecorderPosition): void {
    this._storage.update(position);
  }

  /**
   * Detaches the underlying storage's window `storage` listener. Root-provided,
   * so this never runs in production; specs that construct throwaway instances
   * call it in teardown so the listeners do not accumulate across tests.
   */
  destroy(): void {
    this._storage.destroy();
  }
}

/**
 * Pure geometry/validation for the recorder card's bottom-right-anchored drag
 * offset, shared by the storage service (read validation) and the recorder
 * component (clamping on drag and resize).
 */
export class RecorderPosition {
  /**
   * Clamps a stored recorder offset so the card stays fully on-screen after a
   * viewport resize. The lower bound uses `Math.min(0, …)` to prevent bound
   * inversion when the viewport is narrower or shorter than the card: in that
   * case the axis collapses to the anchored `0` offset rather than a positive
   * (off-screen) value.
   */
  static clampToViewport(
    position: IRecorderPosition,
    cardRect: { width: number; height: number },
    viewport: { width: number; height: number }
  ): IRecorderPosition {
    const minX = Math.min(
      0,
      -(viewport.width - cardRect.width - VIEWPORT_MARGIN_PX)
    );
    const minY = Math.min(
      0,
      -(viewport.height - cardRect.height - VIEWPORT_MARGIN_PX)
    );
    return {
      x: RecorderPosition._clampBetween(position.x, minX, 0),
      y: RecorderPosition._clampBetween(position.y, minY, 0),
    };
  }

  /**
   * Whether a stored coordinate is a legitimate free-drag offset: finite and
   * `<= 0`, since the card only ever moves left and up from its anchor.
   */
  static isValidOffset(value: unknown): value is number {
    return typeof value === 'number' && isFinite(value) && value <= 0;
  }

  private static _clampBetween(
    value: number,
    minimum: number,
    maximum: number
  ): number {
    return Math.min(Math.max(value, minimum), maximum);
  }
}
